import { useErrorHandlingMutation } from '@backoffice-frontend/data-fetching'
import {
  FormTextarea,
  LoadingButton,
  useForm,
} from '@backoffice-frontend/forms'
import {
  type Theme,
  useMoiaSuccessNotification,
} from '@backoffice-frontend/patterns'
import { usePaceValidators } from '@backoffice-frontend/validators'
import { Button, Checkbox, Dialog, ProgressBar, Text } from '@moia-dev/pace-web'
import { Controller } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { isImpersonationSession } from '../sessions'
import { useCountdown } from '../useCountdown'
import { useCloseSessionMutation } from './CloseSession.hook'
import { formatRemainingTime, getRemainingFraction } from './utils'

export type EndSessionDialogSession = {
  id: string
  /** `impersonation` or `scopedAccess` — see {@link isImpersonationSession}. */
  type: string
  createdAt: string
  expiresAt: string
}

type EndSessionDialogProps = {
  /** Dialog ID, provided by `useDialog`. */
  id: string
  session: EndSessionDialogSession
  /** Dismisses the dialog (cancel button, close icon, or after a successful end). */
  onClose: VoidFunction
  /** Runs after the session was ended successfully, e.g. to redirect or refetch. */
  onSessionEnded?: VoidFunction
  /**
   * When `true`, an expired session can no longer be dismissed — the cancel
   * button and close icon are hidden and the actor must end the session. Used by
   * the Backoffice session frame; the Admin Panel list keeps sessions dismissible.
   */
  blockOnExpiry?: boolean
}

type EndSessionFormValues = {
  dataModified: boolean
  notes: string
}

const styles = {
  form: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.space.Space4,
  }),
  footer: (theme: Theme) => ({
    display: 'flex',
    gap: theme.space.Space2,
    justifyContent: 'space-between',
  }),
}

export const EndSessionDialog = ({
  id,
  session,
  onClose,
  onSessionEnded,
  blockOnExpiry = false,
}: EndSessionDialogProps) => {
  const { t } = useTranslation()
  const { required } = usePaceValidators()
  const { enqueueMoiaSuccessNotification } = useMoiaSuccessNotification()

  const secondsRemaining = useCountdown(session.expiresAt)
  const hasExpired = secondsRemaining <= 0
  // The bar fills as the session runs down: empty at the start, full at expiry.
  const elapsedFraction =
    1 -
    getRemainingFraction(session.createdAt, session.expiresAt, secondsRemaining)
  const isImpersonation = isImpersonationSession(session)

  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm<EndSessionFormValues>({
    defaultValues: { dataModified: true, notes: '' },
    mode: 'onChange',
  })

  const dataModified = watch('dataModified')
  const notes = watch('notes')

  const [closeSession] = useErrorHandlingMutation(useCloseSessionMutation)

  // Ending is blocked until the actor either confirms no data was changed or
  // documents the changes they made — mirrors the Figma default view.
  // `notes` is `undefined` while the textarea is unregistered (checkbox off),
  // so it is coalesced before trimming.
  const canEnd = !dataModified || (notes ?? '').trim().length > 0
  const canDismiss = !(blockOnExpiry && hasExpired)

  const onSubmit = async ({ dataModified, notes }: EndSessionFormValues) => {
    const result = await closeSession({
      variables: {
        input: {
          sessionId: session.id,
          customerDataModified: dataModified,
          closureNotes: dataModified ? (notes ?? '').trim() : null,
        },
      },
    })

    if (result.data?.closeTemporaryBackofficeSession) {
      enqueueMoiaSuccessNotification(t('tbs_endSession_notification'))
      onSessionEnded?.()
      onClose()
    }
  }

  return (
    <Dialog
      id={id}
      title={
        isImpersonation
          ? t('tbs_endSession_impersonation_title')
          : t('tbs_endSession_scopedAccess_title')
      }
      onClose={canDismiss ? onClose : undefined}
      css={{ minWidth: 480 }}
      footer={
        <div css={styles.footer}>
          {canDismiss && (
            <Button
              onClick={onClose}
              label={t('common_cancel_button')}
              size="compact"
              variant="secondary"
              disabled={isSubmitting}
            />
          )}
          <LoadingButton
            type="submit"
            form={id}
            size="compact"
            css={{ marginLeft: 'auto' }}
            disabled={!canEnd}
            loading={isSubmitting}
            label={t('tbs_endSession_end_button')}
          />
        </div>
      }
    >
      <form id={id} onSubmit={handleSubmit(onSubmit)} css={styles.form}>
        <div>
          <Text size="sm">
            <Trans
              i18nKey={t('tbs_endSession_remainingTime_label')}
              values={{
                time: formatRemainingTime(secondsRemaining, session.type),
              }}
              components={{ b: <b /> }}
            />
          </Text>
          <ProgressBar
            value={elapsedFraction}
            max={1}
            variant={hasExpired ? 'critical' : 'default'}
            ariaLabel={t('tbs_endSession_remainingTime_ariaLabel')}
          />
        </div>

        <Controller
          control={control}
          name="dataModified"
          render={({ field }) => (
            <Checkbox
              checked={field.value}
              onChange={event => field.onChange(event.target.checked)}
              label={
                isImpersonation
                  ? t('tbs_endSession_customerDataModified_checkbox')
                  : t('tbs_endSession_organizationDataModified_checkbox')
              }
            />
          )}
        />

        {dataModified && (
          <FormTextarea
            control={control}
            name="notes"
            shouldUnregister
            rules={{ required }}
            label={t('tbs_endSession_performedChanges_label')}
            placeholder={t('tbs_endSession_performedChanges_placeholder')}
            disabled={isSubmitting}
          />
        )}
      </form>
    </Dialog>
  )
}
